class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

// Custom cacao polyfill

if (!window.__locationChangePatched) {
  const _ps = history.pushState, _rs = history.replaceState;
  history.pushState = function(){ const r=_ps.apply(this,arguments); window.dispatchEvent(new Event('locationchange')); return r; };
  history.replaceState = function(){ const r=_rs.apply(this,arguments); window.dispatchEvent(new Event('locationchange')); return r; };
  window.addEventListener('popstate', () => window.dispatchEvent(new Event('locationchange')));
  window.__locationChangePatched = true;
}

// Custom cacao slider filter class

class CacaoRange extends HTMLElement {
  constructor() {
    super();

    const paramName = this.dataset.param;
    const values = JSON.parse(this.dataset.values).map(Number).sort((a,b)=>a-b);
    const minVal = values[0], maxVal = values[values.length - 1];

    const inputMin = this.querySelector(".input-min");
    const inputMax = this.querySelector(".input-max");
    const rangeMin = this.querySelector(".range-min");
    const rangeMax = this.querySelector(".range-max");
    const progress = this.querySelector(".progress");

    // --- state ---
    let liveAdjust = false;
    let debounceTimer = null;
    let suppressRender = false;   // kad samo sinhronizujemo UI iz URL-a
    let userTouched = false;      // da li je korisnik zaista koristio slider

    // --- hidden inputs (da drugi filteri pošalju i cacao izbor kad je aktivan) ---
    const formEl = this.closest('form');
    let hiddenBucket = null;

    const ensureHiddenBucket = () => {
      if (!formEl) return null;

      // 1) ukloni SVE prethodne hidden inpute sa istim name iz forme (iz bilo koje stare instance)
      formEl.querySelectorAll(`input[type="hidden"][name="${paramName}"]`).forEach((el) => el.remove());

      // 2) pokušaj da nađeš već postojeći bucket za ovaj param (posle re-rendera)
      hiddenBucket = formEl.querySelector(`div[data-cacao-hidden][data-param="${paramName}"]`);

      // 3) ako ne postoji – napravi novi i obeleži ga ovim paramom (da ga sledeća instanca može pronaći)
      if (!hiddenBucket) {
        hiddenBucket = document.createElement('div');
        hiddenBucket.setAttribute('data-cacao-hidden', '');
        hiddenBucket.setAttribute('data-param', paramName);
        hiddenBucket.style.display = 'none';
        formEl.appendChild(hiddenBucket);
      }

      // (bucket je sada prazan jer smo iz 1) izbrisali stare inpute)
      hiddenBucket.innerHTML = '';
      return hiddenBucket;
    };

    const setHiddenInputs = (vals) => {
      const bucket = ensureHiddenBucket();
      if (!bucket) return;
      vals.forEach((v) => {
        const i = document.createElement('input');
        i.type = 'hidden';
        i.name = paramName;
        i.value = String(v);
        bucket.appendChild(i);
      });
    };

    const clearHiddenInputs = () => {
      if (!formEl) return;
      formEl.querySelectorAll(`input[type="hidden"][name="${paramName}"]`).forEach((el) => el.remove());
      const bucket = formEl.querySelector(`div[data-cacao-hidden][data-param="${paramName}"]`);
      if (bucket) bucket.innerHTML = '';
    };

    // --- helpers ---
    const isDefaultRange = (mn, mx) => mn <= minVal && mx >= maxVal;

    const moveProgress = (mn, mx) => {
      const total = (maxVal - minVal) || 1;
      const trackRect = progress.parentElement.getBoundingClientRect();
      const trackWidth = trackRect.width;

      const cssThumb  = parseFloat(getComputedStyle(this).getPropertyValue('--thumb')) || 18;
      const progressH = progress.getBoundingClientRect().height || 6;
      const inset = Math.max(0, (cssThumb - progressH) / 2);

      const leftPx  = ((mn - minVal) / total) * trackWidth;
      const rightPx = ((maxVal - mx)  / total) * trackWidth;

      const left  = Math.max(0, leftPx  - inset);
      const right = Math.max(0, rightPx - inset);

      progress.style.left  = left + 'px';
      progress.style.right = right + 'px';
    };

    const getBaseSearchParams = () => {
      const base = (window.FacetFiltersForm && FacetFiltersForm.searchParamsPrev) || window.location.search.slice(1);
      return new URLSearchParams(base);
    };

    // Vrati {search, selected} gde selected postoji SAMO ako nije default raspon
    const buildSearchParamsWithSelected = (mn, mx) => {
      const usp = getBaseSearchParams();

      // ukloni stare cacao parametre
      const toDrop = [];
      usp.forEach((v, k) => { if (k === paramName) toDrop.push(k); });
      toDrop.forEach(k => usp.delete(k));

      let selected = [];
      if (!isDefaultRange(mn, mx)) {
        selected = values.filter(v => v >= mn && v <= mx);
        selected.forEach(v => usp.append(paramName, String(v)));
      }
      return { search: usp.toString(), selected };
    };

    const scheduleRender = (mn, mx, immediate = false) => {
      const { search, selected } = buildSearchParamsWithSelected(mn, mx);

      // Hidden inputs: samo kad postoji AKTIVAN opseg (ne default)
      if (selected.length) setHiddenInputs(selected);
      else clearHiddenInputs();

      // Ažuriraj Dawn-ov "last known" query
      if (window.FacetFiltersForm) FacetFiltersForm.searchParamsPrev = search;

      const doRender = () => {
        if (window.FacetFiltersForm && typeof FacetFiltersForm.renderPage === 'function') {
          FacetFiltersForm.renderPage(search, null, true);
        } else {
          window.location.assign(`${window.location.pathname}?${search}`);
        }
      };

      clearTimeout(debounceTimer);
      if (immediate) doRender();
      else debounceTimer = setTimeout(doRender, 300);
    };

    const coerceGap = (mn, mx, fromMin) => {
      if (mx - mn < 1) {
        if (fromMin) mn = mx - 1;
        else mx = mn + 1;
      }
      return [mn, mx];
    };

    // --- sync iz URL-a: postavi UI i (po potrebi) hidden inputs ---
    const syncFromURL = () => {
      const usp = new URLSearchParams(window.location.search.slice(1));
      const chosen = [];
      usp.forEach((v, k) => {
        if (k === paramName) {
          const n = Number(v);
          if (!Number.isNaN(n)) chosen.push(n);
        }
      });

      const mn = chosen.length ? Math.max(minVal, Math.min(...chosen)) : minVal;
      const mx = chosen.length ? Math.min(maxVal, Math.max(...chosen)) : maxVal;

      suppressRender = true;
      rangeMin.value = inputMin.value = mn;
      rangeMax.value = inputMax.value = mx;
      moveProgress(mn, mx);
      suppressRender = false;

      // Hidden inputs samo ako cacao postoji u URL-u (korisnik ga je koristio)
      if (chosen.length) {
        // koristimo baš izabrane vrednosti iz URL-a, da bude 1:1
        setHiddenInputs(chosen.sort((a,b)=>a-b));
      } else {
        clearHiddenInputs();
      }
    };

    // --- INIT ---
    [inputMin.min, rangeMin.min] = [minVal, minVal];
    [inputMax.max, rangeMax.max] = [maxVal, maxVal];
    [rangeMin.max, rangeMax.min] = [maxVal, minVal];
    syncFromURL(); // ako nema cacao u URL-u -> min/max i bez hidden inputa

    // --- events ---
    const update = (e) => {
      if (suppressRender) return;

      const isFromMin = e?.target === rangeMin || e?.target === inputMin;
      let mn = Number(rangeMin.value);
      let mx = Number(rangeMax.value);
      [mn, mx] = coerceGap(mn, mx, isFromMin);

      inputMin.value = mn;
      inputMax.value = mx;
      moveProgress(mn, mx);

      if (!liveAdjust) scheduleRender(mn, mx, false);
    };

    const onPointerStart = () => { userTouched = true; liveAdjust = true; clearTimeout(debounceTimer); };
    const onPointerEnd   = () => {
      liveAdjust = false;
      const mn = Number(rangeMin.value);
      const mx = Number(rangeMax.value);
      // Ako korisnik vrati na default, ovo će obrisati cacao parametre/hidden inputs
      scheduleRender(mn, mx, true);
    };

    rangeMin.addEventListener("input", update);
    rangeMax.addEventListener("input", update);
    rangeMin.addEventListener("pointerdown", onPointerStart);
    rangeMax.addEventListener("pointerdown", onPointerStart);
    rangeMin.addEventListener("pointerup", onPointerEnd);
    rangeMax.addEventListener("pointerup", onPointerEnd);
    rangeMin.addEventListener("mouseup", onPointerEnd);
    rangeMax.addEventListener("mouseup", onPointerEnd);
    rangeMin.addEventListener("touchend", onPointerEnd, { passive: true });
    rangeMax.addEventListener("touchend", onPointerEnd, { passive: true });

    inputMin.addEventListener("change", () => {
      userTouched = true;
      rangeMin.value = Math.max(minVal, Math.min(Number(inputMin.value || minVal), Number(rangeMax.value) - 1));
      update({ target: inputMin });
    });
    inputMax.addEventListener("change", () => {
      userTouched = true;
      rangeMax.value = Math.min(maxVal, Math.max(Number(inputMax.value || maxVal), Number(rangeMin.value) + 1));
      update({ target: inputMax });
    });

    // posle „Remove all”, gašenja pilule, back/forward, AJAX rerendera
    window.addEventListener('locationchange', syncFromURL);
  }
}
customElements.define("cacao-range", CacaoRange);
//Working version